import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { syncLedger, money, type SyncResult, type SyncSection } from "@/lib/financialLedger";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onImported: () => void;
}

function SectionBlock({ title, s }: { title: string; s: SyncSection }) {
  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="font-medium text-sm">{title}</p>
        <div className="flex gap-1.5">
          <Badge variant="secondary">{s.found} found</Badge>
          <Badge variant="outline">{s.matched.length} already in ledger</Badge>
          <Badge>{s.dry_run ? s.entries_pending : s.entries_created} to create</Badge>
        </div>
      </div>
      {s.created.length > 0 && (
        <ul className="text-xs text-muted-foreground space-y-0.5 max-h-36 overflow-y-auto">
          {s.created.map((c, i) => (
            <li key={`${c.id}-${i}`}>
              {c.date} · {c.label} · {money(c.amount_cents ?? 0)}
            </li>
          ))}
        </ul>
      )}
      {s.excluded.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">{s.excluded.length} excluded</summary>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">
            {s.excluded.map((c, i) => <li key={`${c.id}-${i}`}>{c.label} — {c.reason}</li>)}
          </ul>
        </details>
      )}
      {s.needs_review.length > 0 && (
        <Alert variant="destructive" className="py-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="text-xs">{s.needs_review.length} need your review</AlertTitle>
          <AlertDescription className="text-xs">
            <ul className="space-y-0.5">
              {s.needs_review.map((c, i) => <li key={`${c.id}-${i}`}>{c.label} — {c.reason}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

export function BackfillDialog({ open, onOpenChange, onImported }: Props) {
  const { toast } = useToast();
  const [preview, setPreview] = useState<SyncResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      setPreview(await syncLedger(true));
    } catch (e) {
      toast({ title: "Preview failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const r = await syncLedger(false);
      const total = r.registrations.entries_created + r.payroll.entries_created + r.grace.entries_created;
      toast({ title: `${total} transaction${total === 1 ? "" : "s"} imported` });
      setPreview(r);
      onImported();
    } catch (e) {
      toast({ title: "Import failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setCommitting(false);
    }
  };

  const pending = preview
    ? preview.registrations.entries_pending + preview.payroll.entries_pending + preview.grace.entries_pending
    : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setPreview(null); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import existing records into the ledger</DialogTitle>
          <DialogDescription>
            Reviews every registration payment, refund, payroll payment, and Grace payment dated on or after
            July 1, 2026. Preview first — nothing is written until you confirm. Original dates and amounts are
            preserved exactly; nothing is recalculated or guessed.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <Button onClick={runPreview} disabled={loading}>
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reviewing records…</> : "Run preview"}
          </Button>
        )}

        {preview && (
          <div className="space-y-3">
            <SectionBlock title="Registration payments and refunds" s={preview.registrations} />
            <SectionBlock title="Payroll payments" s={preview.payroll} />
            <SectionBlock title="Grace Community payments" s={preview.grace} />

            {preview.dry_run ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Preview only — nothing has been written</AlertTitle>
                <AlertDescription>
                  {pending} transaction{pending === 1 ? "" : "s"} will be created if you continue.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Import complete</AlertTitle>
                <AlertDescription>Records were imported and linked to their source.</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {preview?.dry_run && (
            <Button onClick={commit} disabled={committing || pending === 0}>
              {committing ? "Importing…" : `Import ${pending} transaction${pending === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
